import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { X, Save, User, FileText, ChevronDown, ChevronUp } from "lucide-react";
import { useConciergeTestConfig } from "@/hooks/useConciergeTests";
import PatientSelector from "@/components/clinical-notes/PatientSelector";
import apiClient from "@/api/apiClient";
import {
  validateSouthAfricanId,
  extractDateOfBirth,
} from "@/utils/saIdValidation";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

// Zod validation schema
const requestFormSchema = z
  .object({
    patient_id: z.string().min(1, "Please select a patient"),
    patient_identification_number: z
      .string()
      .optional()
      .refine(
        (value) => {
          // If no value provided, it's optional so valid
          if (!value || value.trim() === "") return true;

          // If value provided, it must be a valid SA ID
          const validation = validateSouthAfricanId(value);
          return validation.isValid;
        },
        {
          message: "Please enter a valid South African ID number (13 digits)",
        }
      ),
    patient_name: z.string().min(1, "Patient name is required"),
    patient_dob: z.string().optional(),
    patient_address: z.string().min(1, "Patient address is required"),
    patient_phone: z.string().min(1, "Patient phone number is required"),
    patient_email: z.string().optional(),
    referring_doctor: z.string().min(1, "Referring doctor is required"),
    requested_tests: z.array(z.string()),
    other_tests: z.string().optional(),
    preferred_date: z
      .string()
      .min(1, "Preferred date is required")
      .refine((date) => {
        const selectedDate = new Date(date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return selectedDate >= today;
      }, "Preferred date cannot be in the past"),
    preferred_time_slot: z.string().min(1, "Preferred time slot is required"),
    clinical_notes: z.string().optional(),
  })
  .refine(
    (data) => {
      return (
        data.requested_tests.length > 0 ||
        (data.other_tests && data.other_tests.trim().length > 0)
      );
    },
    {
      message: "Please select at least one test or specify other tests",
      path: ["requested_tests"],
    }
  );

export default function RequestForm({ request, onSave, onClose }) {
  const [expandedCategories, setExpandedCategories] = useState({});

  const form = useForm({
    resolver: zodResolver(requestFormSchema),
    defaultValues: {
      patient_id: "",
      patient_identification_number: "",
      patient_name: "",
      patient_dob: "",
      patient_address: "",
      patient_phone: "",
      patient_email: "",
      referring_doctor: "",
      requested_tests: [],
      other_tests: "",
      preferred_date: "",
      preferred_time_slot: "",
      clinical_notes: "",
    },
  });

  // Fetch test configuration from backend
  const { data: testConfig, isLoading: configLoading } =
    useConciergeTestConfig();

  // Fetch patients for the selector
  const { data: patients = [], isLoading: patientsLoading } = useQuery({
    queryKey: ["patients"],
    queryFn: () => apiClient.get("/api/patients"),
  });

  useEffect(() => {
    if (request) {
      form.reset({
        ...request,
        patient_dob: request.patient_dob
          ? new Date(request.patient_dob).toISOString().split("T")[0]
          : "",
        preferred_date: request.preferred_date
          ? new Date(request.preferred_date).toISOString().split("T")[0]
          : "",
        other_tests: "", // Reset this field as it's used differently for new inputs
      });
    }
  }, [request, form]);

  const handlePatientChange = (patientId) => {
    const selectedPatient = patients.find((p) => p.id === patientId);
    if (selectedPatient) {
      form.setValue("patient_id", patientId);
      form.setValue(
        "patient_identification_number",
        selectedPatient.identificationNumber || ""
      );
      form.setValue("patient_name", selectedPatient.name || "");
      form.setValue(
        "patient_dob",
        selectedPatient.dateOfBirth
          ? new Date(selectedPatient.dateOfBirth).toISOString().split("T")[0]
          : ""
      );
      form.setValue("patient_address", selectedPatient.address || "");
      form.setValue("patient_phone", selectedPatient.phone || "");
      form.setValue("patient_email", selectedPatient.email || "");

      // Trigger validation for auto-populated fields
      form.trigger(["patient_name", "patient_phone", "patient_address"]);
    } else {
      // Clear patient fields if no patient selected
      form.setValue("patient_id", "");
      form.setValue("patient_identification_number", "");
      form.setValue("patient_name", "");
      form.setValue("patient_dob", "");
      form.setValue("patient_address", "");
      form.setValue("patient_phone", "");
      form.setValue("patient_email", "");
    }
  };

  const handleTestChange = (testCode, checked) => {
    const currentTests = form.getValues("requested_tests");
    const newTests = checked
      ? [...currentTests, testCode]
      : currentTests.filter((t) => t !== testCode);
    form.setValue("requested_tests", newTests);
    form.trigger("requested_tests");
  };

  const handlePanelSelect = (panelTests) => {
    const currentTests = form.getValues("requested_tests");
    const newTests = [
      ...new Set([...currentTests, ...panelTests.map((t) => t.code)]),
    ];
    form.setValue("requested_tests", newTests);
    form.trigger("requested_tests");
  };

  const toggleCategoryExpansion = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const clearAllTests = () => {
    form.setValue("requested_tests", []);
    form.trigger("requested_tests");
  };

  const handleIdNumberChange = (value) => {
    form.setValue("patient_identification_number", value);

    // If ID is valid and no DOB is set, auto-populate it
    const currentDob = form.getValues("patient_dob");
    if (!currentDob && value) {
      const extractedDob = extractDateOfBirth(value);
      if (extractedDob) {
        const dobString = extractedDob.toISOString().split("T")[0];
        form.setValue("patient_dob", dobString);
      }
    }

    // Trigger validation
    form.trigger("patient_identification_number");
  };

  const onSubmit = (data) => {
    const finalData = { ...data };
    if (finalData.other_tests) {
      finalData.requested_tests.push(`Other: ${finalData.other_tests}`);
    }
    delete finalData.other_tests;
    onSave(finalData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-4xl bg-white/90 backdrop-blur-xl border-slate-200/60 shadow-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-white/80 backdrop-blur-sm z-10 border-b">
          <CardTitle>
            {request ? "Edit" : "New"} Concierge Testing Request
          </CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent className="p-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
              {/* Patient Selection */}
              <section>
                <h3 className="text-lg font-semibold text-slate-900 mb-4 border-b pb-2 flex items-center gap-2">
                  <User /> Patient Selection
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <FormField
                      control={form.control}
                      name="patient_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Select Patient *</FormLabel>
                          <FormControl>
                            <PatientSelector
                              patients={patients}
                              selectedPatient={field.value}
                              onPatientChange={(value) => {
                                field.onChange(value);
                                handlePatientChange(value);
                              }}
                              label=""
                              placeholder="Search for a patient..."
                              showAll={false}
                            />
                          </FormControl>
                          <FormMessage />
                          {patientsLoading && (
                            <p className="text-sm text-slate-500 mt-1">
                              Loading patients...
                            </p>
                          )}
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </section>

              {/* Patient Details - Auto-populated */}
              {form.watch("patient_id") && (
                <section>
                  <h3 className="text-lg font-semibold text-slate-900 mb-4 border-b pb-2">
                    Patient Details (Auto-populated)
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50 p-4 rounded-lg">
                    <FormField
                      control={form.control}
                      name="patient_name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Patient Name *</FormLabel>
                          <FormControl>
                            <Input {...field} className="bg-white" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="patient_identification_number"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>ID Number</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              onChange={(e) =>
                                handleIdNumberChange(e.target.value)
                              }
                              placeholder="1234567890123"
                              maxLength={13}
                              className="bg-white"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="patient_dob"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Date of Birth</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              type="date"
                              className="bg-white"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="patient_phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone *</FormLabel>
                          <FormControl>
                            <Input {...field} type="tel" className="bg-white" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="patient_address"
                      render={({ field }) => (
                        <FormItem className="md:col-span-2">
                          <FormLabel>Home Address *</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="Street, City, State, Zip"
                              className="bg-white"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="patient_email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              type="email"
                              className="bg-white"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <p className="text-sm text-slate-600 mt-2">
                    Patient details are automatically filled from their profile.
                    You can edit them if needed.
                  </p>
                </section>
              )}

              {/* Request Details */}
              <section>
                <h3 className="text-lg font-semibold text-slate-900 mb-4 border-b pb-2 flex items-center gap-2">
                  <FileText /> Request Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="preferred_date"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Preferred Date *</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="date"
                            min={new Date().toISOString().split("T")[0]}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="preferred_time_slot"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Preferred Time *</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select time slot" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="morning">
                              Morning (8am - 12pm)
                            </SelectItem>
                            <SelectItem value="afternoon">
                              Afternoon (12pm - 4pm)
                            </SelectItem>
                            <SelectItem value="evening">
                              Evening (4pm - 8pm)
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="referring_doctor"
                    render={({ field }) => (
                      <FormItem className="md:col-span-2">
                        <FormLabel>Referring Doctor *</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </section>

              {/* Loading state for test configuration */}
              {configLoading && (
                <section>
                  <div className="flex items-center justify-center h-32">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                      <p className="text-slate-600 text-sm">
                        Loading test configuration...
                      </p>
                    </div>
                  </div>
                </section>
              )}

              {/* Test Panels */}
              {testConfig && (
                <section>
                  <h3 className="text-lg font-semibold text-slate-900 mb-4 border-b pb-2">
                    Quick Test Panels
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4 mb-6">
                    {testConfig.testPanels.map((panel, index) => (
                      <Card
                        key={index}
                        className="border-2 border-dashed border-slate-200 hover:border-blue-300 transition-colors cursor-pointer"
                      >
                        <CardContent className="p-4">
                          <h4 className="font-semibold text-slate-900 mb-2">
                            {panel.name}
                          </h4>
                          <p className="text-xs text-slate-600 mb-3">
                            {panel.tests.length} tests included
                          </p>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handlePanelSelect(panel.tests)}
                            className="w-full"
                          >
                            Add Panel
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={clearAllTests}
                      className="text-red-600 hover:text-red-700"
                    >
                      Clear All Tests
                    </Button>
                  </div>
                </section>
              )}

              {/* Individual Tests by Category */}
              {testConfig && (
                <section>
                  <h3 className="text-lg font-semibold text-slate-900 mb-4 border-b pb-2">
                    Available Tests by Category{" "}
                    {form.watch("requested_tests").length > 0 &&
                      `(${form.watch("requested_tests").length} selected)`}
                  </h3>
                  {form.formState.errors.requested_tests && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-600">
                        {form.formState.errors.requested_tests.message}
                      </p>
                    </div>
                  )}
                  <div className="space-y-4">
                    {Object.entries(testConfig.availableTests).map(
                      ([category, tests]) => {
                        const categoryName = category
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, (l) => l.toUpperCase());
                        const isExpanded = expandedCategories[category];

                        // Count selected tests in this category
                        const selectedInCategory = tests.filter((test) =>
                          form
                            .watch("requested_tests")
                            .includes(`${test.code}-${test.name}`)
                        ).length;

                        return (
                          <Collapsible
                            key={category}
                            open={isExpanded}
                            onOpenChange={() =>
                              toggleCategoryExpansion(category)
                            }
                          >
                            <CollapsibleTrigger asChild>
                              <Card className="cursor-pointer hover:bg-slate-50 transition-colors">
                                <CardContent className="p-4">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                                        {categoryName}
                                        {selectedInCategory > 0 && (
                                          <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-medium text-white bg-blue-500 rounded-full">
                                            {selectedInCategory}
                                          </span>
                                        )}
                                      </h4>
                                      <p className="text-sm text-slate-600">
                                        {selectedInCategory > 0 ? (
                                          <>
                                            {selectedInCategory} of{" "}
                                            {tests.length} tests selected
                                          </>
                                        ) : (
                                          <>{tests.length} tests available</>
                                        )}
                                      </p>
                                    </div>
                                    {isExpanded ? (
                                      <ChevronUp className="w-5 h-5 text-slate-400" />
                                    ) : (
                                      <ChevronDown className="w-5 h-5 text-slate-400" />
                                    )}
                                  </div>
                                </CardContent>
                              </Card>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                              <div className="mt-2 p-4 bg-slate-50 rounded-lg border">
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                  {tests.map((test) => {
                                    const isSelected = form
                                      .watch("requested_tests")
                                      .includes(`${test.code}-${test.name}`);
                                    return (
                                      <div
                                        key={`${test.code}-${test.name}`}
                                        className={`flex items-start space-x-2 p-3 rounded-lg border bg-white ${isSelected ? "border-blue-200 bg-blue-50" : "border-slate-200"}`}
                                      >
                                        <Checkbox
                                          id={`${test.code}-${test.name}`}
                                          checked={isSelected}
                                          onCheckedChange={(checked) =>
                                            handleTestChange(
                                              `${test.code}-${test.name}`,
                                              checked
                                            )
                                          }
                                          className="mt-1"
                                        />
                                        <div className="flex-1 min-w-0">
                                          <Label
                                            htmlFor={`${test.code}-${test.name}`}
                                            className="font-normal text-sm cursor-pointer block"
                                          >
                                            <div className="font-medium text-slate-900">
                                              {test.name}
                                            </div>
                                            <div className="text-xs text-slate-500 mt-1">
                                              Code: {test.code} | Specimen:{" "}
                                              {test.specimen}
                                            </div>
                                          </Label>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </CollapsibleContent>
                          </Collapsible>
                        );
                      }
                    )}
                  </div>
                  <FormField
                    control={form.control}
                    name="other_tests"
                    render={({ field }) => (
                      <FormItem className="mt-4">
                        <FormLabel>Other Tests (specify)</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="List any additional tests not shown above"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </section>
              )}

              {/* Selected Tests Summary */}
              {testConfig && form.watch("requested_tests").length > 0 && (
                <section>
                  <h3 className="text-lg font-semibold text-slate-900 mb-4 border-b pb-2">
                    Selected Tests ({form.watch("requested_tests").length})
                  </h3>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {form.watch("requested_tests").map((testCode) => {
                        // Find the test in all categories
                        let foundTest = null;
                        Object.values(testConfig.availableTests).forEach(
                          (tests) => {
                            const testCodeWithoutName = testCode.split("-")[0];
                            const testName = testCode.split("-")[1];
                            const test = tests.find(
                              (t) =>
                                t.code === testCodeWithoutName &&
                                t.name === testName
                            );
                            if (test) foundTest = test;
                          }
                        );
                        return (
                          <div key={testCode} className="text-sm">
                            <span className="font-medium text-slate-900">
                              {foundTest ? foundTest.name : testCode}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </section>
              )}

              {/* Clinical Notes */}
              <section>
                <h3 className="text-lg font-semibold text-slate-900 mb-4 border-b pb-2">
                  Clinical Notes
                </h3>
                <FormField
                  control={form.control}
                  name="clinical_notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder="Add any relevant clinical notes, reason for testing, or special instructions for the nurse..."
                          rows={4}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </section>

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-6 border-t">
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-gradient-to-r from-blue-600 to-indigo-600"
                >
                  <Save className="w-4 h-4 mr-2" /> Submit Request
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
